<script>
import { mapState, mapActions, mapMutations } from "vuex";
import FooterWrapper from "@/components/FooterWrapper";

export default {
  components: {
    FooterWrapper,
  },

  data() {
    return {
      contract: {},
      loading: false,
    };
  },

  computed: {
    ...mapState(["teamContracts", "activeTeamId"]),

    //Calculate total insurance charge with quantity times insurance per machine
    insuranceCharge: function () {
      let totalInsurance =
        this.contract.machine_insurance * this.contract.machine_count;
      return totalInsurance;
    },
    //Calculate total security deposit charge with quantity times security deposit per machine
    securityDepositCharge: function () {
      let totalCharge =
        this.contract.machine_security_deposit *
        this.contract.machine_count *
        1;
      return totalCharge;
    },
    //Calculate total set up fee charge with quantity times set up fees per machine
    setUpFeeCharge: function () {
      let totalSetUpFee =
        this.contract.machine_setup_fee * this.contract.machine_count;
      return totalSetUpFee;
    },
    //Calculate total charge
    oneTimeChargeTotal: function () {
      let total =
        this.setUpFeeCharge + this.securityDepositCharge + this.insuranceCharge;
      return total.toFixed(2);
    },
    //calculate credit card servicing fee
    creditCardFee: function () {
      let fee = this.oneTimeChargeTotal * 0.05;
      return fee.toFixed(2);
    },
  },
  methods: {
    async submit() {
      this.loading = true;
      // Create the one time invoice and schedule to be charged automatically in one hour (Stripe default)
      let invoice = await this.createOneTimeCharge({ contract: this.contract });
      await this.getTeamContracts();
      this.contract = this.teamContracts[0];
      if (invoice.ok) {
        let success = await this.putContractAsOnboardingUser({
          teamId: this.activeTeamId,
          contractId: this.teamContracts[0].id,
          // Here we set the onboarding_invoice_created to true
          // This function fires when it receives an event from the backend that the stripe invoice was created
          onboarding_invoice_created: true,
          payment_method_ingested:
            this.teamContracts[0].payment_method_ingested,
          pool_info_captured: this.teamContracts[0].pool_info_captured,
        });

        if (success) {
          // On success, check the most recently created contract for any other incomplete fields and reroute appropriately
          this.loading = false;
          this.pushToast({
            title: "Onboarding Complete",
            message:
              "An invoice has been created and will be charged automatically to your default payment method",
            variant: "success",
          });
          if (!this.teamContracts[0].compatible) {
            this.$router.push({ path: "/onboarding-complete" });
          } else if (
            this.teamContracts[0].compatible &&
            !this.teamContracts[0].pool_info_captured
          ) {
            this.$router.push({ path: "/pool-selection" });
          } else {
            this.$router.push({ path: "/onboarding-complete" });
          }
        }
        this.loading = false;
      }
    },
    ...mapMutations(["pushToast"]),
    ...mapActions([
      "getTeamContracts",
      "createOneTimeCharge",
      "putContractAsOnboardingUser",
    ]),
  },
  //Call the Team Contracts Function to Update the Contract
  async created() {
    await this.getTeamContracts();
    this.contract = this.teamContracts[0];
  },
};
</script>

<template>
  <FooterWrapper>
    <b-container class="mt-3">
      <b-row align-h="center">
        <b-col lg="9" xl="8">
          <div>
            <h1>Confirm Charges</h1>
            <i>
              These charges, outlined in Exhibit A of your contract, cover Set
              Up Fees, a standard Security Deposit, and any insurance you may
              have purchased. All credit card purchases are subject to a 5%
              processing.
            </i>
            <div class="spacer" />
            <b-card>
              <b-card-title>
                Security Deposit, Insurance, and Set Up Fees ({{
                  this.contract.machine_count
                }}
                Units)
              </b-card-title>
              <b-card>
                <b-row>
                  <b-col class="chargeGroup">
                    <b-card-text>Security Deposit</b-card-text>
                  </b-col>
                  <b-col cols="5">
                    <hr class="dashed" />
                  </b-col>
                  <b-col class="chargeGroup">
                    <b-card-text class="charge">
                      ${{ this.securityDepositCharge.toFixed(2) }}
                    </b-card-text>
                  </b-col>
                </b-row>
                <b-row class="details">
                  (${{ this.contract.machine_security_deposit }} x
                  {{ this.contract.machine_count }} {{ this.contract.model }})
                </b-row>
                <div class="spacer" />
                <b-row>
                  <b-col class="chargeGroup">
                    <b-card-text>Insurance</b-card-text>
                  </b-col>
                  <b-col cols="7">
                    <hr class="dashed" />
                  </b-col>
                  <b-col class="chargeGroup">
                    <b-card-text class="charge">
                      ${{ this.insuranceCharge }}
                    </b-card-text>
                  </b-col>
                </b-row>
                <b-row class="details">
                  (${{ this.contract.machine_insurance }} x
                  {{ this.contract.machine_count }} {{ this.contract.model }})
                </b-row>
                <div class="spacer" />
                <b-row>
                  <b-col class="chargeGroup">
                    <b-card-text>Set Up Fee</b-card-text>
                  </b-col>
                  <b-col cols="7">
                    <hr class="dashed" />
                  </b-col>
                  <b-col class="chargeGroup">
                    <b-card-text class="charge">
                      ${{ this.setUpFeeCharge }}
                    </b-card-text>
                  </b-col>
                </b-row>
                <b-row class="details">
                  (${{ this.contract.machine_setup_fee }} x
                  {{ this.contract.machine_count }} {{ this.contract.model }})
                </b-row>
                <hr class="solid" />
                <b-row>
                  <b-col class="totalGroup">
                    <b-card-text>Total</b-card-text>
                  </b-col>
                  <b-col class="chargeGroup">
                    <b-card-text class="total">
                      ${{ this.oneTimeChargeTotal }}
                    </b-card-text>
                  </b-col>
                </b-row>
              </b-card>
              <div class="spacer" />
              <b-button type="submit" variant="primary" @click="submit">
                <div v-if="!loading">Confirm Charges</div>
                <div v-else-if="loading">
                  <b-spinner label="Spinning"></b-spinner>
                </div>
              </b-button>
              <div class="spacer" />
              <b-row>
                <b-col class="disclaimer">
                  <b-card-text class="small-text">
                    Credit Card Payments incur a 5% processing fee (${{
                      this.creditCardFee
                    }}) Linking a bank account will incur a one-time $2 charge
                  </b-card-text>
                </b-col>
              </b-row>
            </b-card>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </FooterWrapper>
</template>

<style scoped>
.charge {
  float: right;
  margin-right: 20px;
  font-size: 20px;
}
.total {
  float: right;
  margin-right: 20px;
  font-size: 30px;
}
.small-text {
  float: left;
  font-size: 14px;
  width: 400px;
}
.spacer {
  height: 10px;
}
.chargeGroup {
  cols: "6";
  font-size: 20px;
}
.totalGroup {
  cols: "6";
  font-size: 30px;
}
hr.solid {
  border-top: 3px solid #bbb;
}
hr.dashed {
  margin-top: 15px;
  border-top: 3px dashed #bbb;
}
.details {
  margin-left: 0px;
}
.disclaimer {
  cols: "3";
}
</style>
